// Pantalla de login del portal (rediseño, docs/PLAN-REDISENO-PORTAL.md
// §6.1): a la izquierda la grilla tecnica, la marca y el tagline; a la
// derecha la caja de ingreso sobre slate, con los dos pasos (contraseña,
// codigo del celular) a la vista antes de que aparezcan. En el celular, solo
// la caja. La maquina de estados de Cognito es la compartida
// (@ucorp/web-auth): los tres caminos del pool administrativo son
//   1. NEW_PASSWORD_REQUIRED: cuenta recien creada por UCORP, elige clave.
//   2. MFA_SETUP: primer ingreso, asocia el segundo factor. Sin QR: el
//      secreto se muestra en texto para tipearlo, y la URL otpauth:// como
//      link, que en el celular abre la app de autenticacion.
//   3. SOFTWARE_TOKEN_MFA: ingresos siguientes, codigo de 6 digitos.
//
// MFA TOTP obligatorio: el operador del proveedor entra por el pool
// administrativo, que lo exige (D1 de ROADMAP-PANEL-PROVEEDOR). Es la
// posicion segura para la cuenta de un tercero.
//
// Registro del copy: impersonal, que es el de las vistas administrativas
// (gestion/07_identidad/readme.md). El voseo es solo de la app del pasajero.
import { useState } from "react";
import { agruparSecreto, useLoginFlow, type TotpBranding } from "@ucorp/web-auth";
import { Alert } from "../components/ui/alert.js";
import { Brand } from "../components/ui/brand.js";
import { Button } from "../components/ui/button.js";
import { cn } from "../components/ui/cn.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";
import type { PortalConfig } from "../config.js";
import { POLITICA_MFA } from "./mfa.js";
import type { PortalSession } from "./session.js";

/** Lo ve el operador en su app TOTP: una vez enrolado, no se renombra. */
const MARCA: TotpBranding = { issuer: "UCORP Proveedores", deviceName: "portal" };

interface Props {
  config: PortalConfig;
  /** Aviso previo, por ejemplo "sesión vencida". */
  notice?: string;
  onLogin: (session: PortalSession) => void;
}

/** Los pasos del ingreso, con el actual marcado y los hechos tildados. */
function Steps({ steps, current }: { steps: readonly string[]; current: number }): React.JSX.Element {
  return (
    <ol aria-label={`Paso ${String(current + 1)} de ${String(steps.length)}`} className="m-0 flex list-none items-center gap-2 p-0 text-eyebrow text-texto-suave">
      {steps.map((step, index) => {
        const done = index < current;
        const on = index === current;
        return (
          <li key={step} className={cn("flex items-center gap-2", index < steps.length - 1 && "flex-1")}>
            <span
              aria-hidden="true"
              className={cn(
                "grid size-[18px] shrink-0 place-items-center rounded-full border text-micro font-semibold",
                on ? "border-acero bg-acero text-blanco" : done ? "border-ok bg-ok/20 text-ok" : "border-linea-fuerte text-texto-suave",
              )}
            >
              {done ? "✓" : String(index + 1)}
            </span>
            <span className={cn("whitespace-nowrap", on && "text-texto-2")}>{step}</span>
            {index < steps.length - 1 && <span aria-hidden="true" className="h-px flex-1 bg-divisor" />}
          </li>
        );
      })}
    </ol>
  );
}

const CODE_INPUT = "tabular h-[52px] text-center text-xl font-semibold tracking-[0.35em]";

export function Login({ config, notice, onLogin }: Props): React.JSX.Element {
  const flujo = useLoginFlow(
    { userPoolId: config.userPoolId, clientId: config.clientId },
    MARCA,
    onLogin,
    POLITICA_MFA,
  );
  const { pantalla, error, sending } = flujo;

  const [email, setEmail] = useState("");
  const [contrasena, setContrasena] = useState("");
  const [nuevaContrasena, setNuevaContrasena] = useState("");
  const [codigo, setCodigo] = useState("");

  const volverAlInicio = (): void => {
    flujo.volverAlInicio();
    setContrasena("");
    setNuevaContrasena("");
    setCodigo("");
  };

  const titulo =
    pantalla.paso === "credenciales" ? "Ingresar"
    : pantalla.paso === "nueva_contrasena" ? "Elegir una contraseña"
    : pantalla.paso === "mfa_codigo" ? "Código del celular"
    : "Activar el código del celular";

  return (
    <div className="grid min-h-full lg:grid-cols-[1.05fr_1fr]">
      {/* Lado de marca: solo en escritorio. Grilla tecnica y dos orbes muy difusos, sin gradientes de marca. */}
      <section aria-hidden="true" className="relative hidden overflow-hidden bg-pagina p-12 lg:flex lg:flex-col lg:justify-between">
        <div className="grilla-tecnica pointer-events-none absolute inset-0" />
        <div className="pointer-events-none absolute -right-24 top-1/3 size-80 rounded-full bg-acero/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-16 bottom-10 size-64 rounded-full bg-ambar/10 blur-3xl" />
        <div className="relative flex items-center justify-between">
          <Brand size="lg" />
          <span className="rotulo text-ambar">Portal del proveedor</span>
        </div>
        <div className="relative max-w-lg">
          <h2 className="m-0 text-[36px] font-semibold leading-apretado tracking-display text-blanco">
            Movilidad industrial,
            <br />
            bajo control.
          </h2>
          <p className="m-0 mt-5 text-base leading-suelto text-texto-suave">
            El despacho de la empresa de transporte, en la misma plataforma que la planta: ofertas con plazo,
            asignación de móvil y conductor, hoja de ruta para el chofer, nómina y vencimientos.
          </p>
          <dl className="m-0 mt-10 grid grid-cols-3 gap-6 border-t border-divisor pt-8">
            {[
              ["Ofertas", "Suenan al llegar, con el plazo a la vista"],
              ["Flota", "Elegibilidad por planta y vencimientos"],
              ["Plantas", "Todas juntas, una a elección"],
            ].map(([k, v]) => (
              <div key={k}>
                <dt className="rotulo text-ambar">{k}</dt>
                <dd className="m-0 mt-1.5 text-sm leading-snug text-texto-2">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <p className="relative m-0 text-xs text-texto-debil">UCORP · staging · Entorno de prueba</p>
      </section>

      <section className="relative flex items-center justify-center p-6 py-12">
        <div className="pointer-events-none absolute inset-0 lg:hidden">
          <div className="grilla-tecnica absolute inset-0" />
        </div>
        <div className="relative flex w-full max-w-[400px] flex-col gap-4 rounded-lg border border-linea bg-superficie p-7">
          <header className="flex flex-col gap-1">
            <div className="mb-3 lg:hidden">
              <Brand size="sm" />
            </div>
            <h1 className="m-0 text-xl font-semibold leading-titulo tracking-titulo text-blanco">{titulo}</h1>
            <p className="m-0 text-sm text-texto-suave">
              {pantalla.paso === "credenciales" && "Cuenta de operador de la empresa de transporte"}
              {pantalla.paso === "nueva_contrasena" &&
                "La cuenta fue creada por UCORP. Elegir una contraseña propia: mínimo 12 caracteres, con mayúsculas, minúsculas y números."}
              {pantalla.paso === "mfa_codigo" && "Abrir la app de autenticación y escribir el código de 6 dígitos de UCORP Proveedores."}
              {pantalla.paso === "mfa_setup" &&
                "El acceso requiere un segundo factor. Cargar este secreto en una app de autenticación (Google Authenticator, Authy o similar) y confirmar con el número que genere."}
            </p>
          </header>

          {notice !== undefined && pantalla.paso === "credenciales" && <Alert variant="info">{notice}</Alert>}
          {error !== null && <Alert variant="error">{error}</Alert>}

          {pantalla.paso === "credenciales" && (
            <form
              className="flex flex-col gap-4"
              onSubmit={(evento) => {
                evento.preventDefault();
                flujo.iniciarSesion(email, contrasena);
              }}
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  aria-invalid={error !== null ? true : undefined}
                  onChange={(evento) => {
                    setEmail(evento.target.value);
                  }}
                  required
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="contrasena">Contraseña</Label>
                <Input
                  id="contrasena"
                  type="password"
                  autoComplete="current-password"
                  value={contrasena}
                  aria-invalid={error !== null ? true : undefined}
                  onChange={(evento) => {
                    setContrasena(evento.target.value);
                  }}
                  required
                />
              </div>
              <Button type="submit" size="lg" block disabled={sending}>
                {sending ? "Ingresando..." : "Ingresar"}
              </Button>
              <Steps steps={["Contraseña", "Código del celular"]} current={0} />
              <p className="m-0 text-xs leading-normal text-texto-suave">
                Después de la contraseña se pide el código de 6 dígitos de la app de autenticación del celular. El primer ingreso lo configura.
              </p>
            </form>
          )}

          {pantalla.paso === "nueva_contrasena" && (
            <form
              className="flex flex-col gap-4"
              onSubmit={(evento) => {
                evento.preventDefault();
                flujo.completarNuevaContrasena(nuevaContrasena);
              }}
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="nueva-contrasena">Contraseña nueva</Label>
                <Input
                  id="nueva-contrasena"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  value={nuevaContrasena}
                  onChange={(evento) => {
                    setNuevaContrasena(evento.target.value);
                  }}
                  required
                />
                <p className="m-0 text-xs text-texto-suave">Se usa solo en este portal. Nadie de UCORP la conoce.</p>
              </div>
              <Button type="submit" size="lg" block disabled={sending}>
                {sending ? "Guardando..." : "Guardar contraseña"}
              </Button>
              <Steps steps={["Cuenta", "Contraseña", "Código del celular"]} current={1} />
            </form>
          )}

          {pantalla.paso === "mfa_setup" && (
            <form
              className="flex flex-col gap-4"
              onSubmit={(evento) => {
                evento.preventDefault();
                flujo.verificarTotpInicial(codigo);
              }}
            >
              <p
                aria-label="Secreto TOTP"
                className="tabular m-0 break-all rounded-md border border-linea-fuerte bg-hundida px-3 py-2.5 text-lg font-semibold tracking-[0.08em] text-texto"
              >
                {agruparSecreto(pantalla.secreto)}
              </p>
              <p className="m-0 text-xs leading-normal text-texto-suave">
                Desde el celular,{" "}
                <a className="text-acero-texto underline underline-offset-2" href={pantalla.urlOtpauth}>
                  abrir directamente en la app de autenticación
                </a>
                . En la app aparece como UCORP Proveedores.
              </p>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="codigo-inicial">Código de 6 dígitos</Label>
                <Input
                  id="codigo-inicial"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="000000"
                  className={CODE_INPUT}
                  value={codigo}
                  onChange={(evento) => {
                    setCodigo(evento.target.value);
                  }}
                  required
                />
              </div>
              <Button type="submit" size="lg" block disabled={sending}>
                {sending ? "Verificando..." : "Activar y entrar"}
              </Button>
              <Steps steps={["Cuenta", "Contraseña", "Código del celular"]} current={2} />
            </form>
          )}

          {pantalla.paso === "mfa_codigo" && (
            <form
              className="flex flex-col gap-4"
              onSubmit={(evento) => {
                evento.preventDefault();
                flujo.responderCodigoMfa(codigo);
              }}
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="codigo">Código de 6 dígitos</Label>
                <Input
                  id="codigo"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="000000"
                  className={CODE_INPUT}
                  value={codigo}
                  onChange={(evento) => {
                    setCodigo(evento.target.value);
                  }}
                  required
                />
                <p className="m-0 text-xs text-texto-suave">El código cambia cada 30 segundos. Si venció, escribir el siguiente.</p>
              </div>
              <Button type="submit" size="lg" block disabled={sending}>
                {sending ? "Verificando..." : "Ingresar"}
              </Button>
              <Steps steps={["Contraseña", "Código del celular"]} current={1} />
            </form>
          )}

          <div className="flex items-center justify-between gap-3 border-t border-divisor pt-3 text-eyebrow text-texto-debil">
            {pantalla.paso !== "credenciales" ? (
              <Button variant="enlace" size="inline" className="text-xs" onClick={volverAlInicio}>
                Volver al inicio de sesión
              </Button>
            ) : (
              <span>UCORP · staging</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
